/**
 * Domain error codes. Every code needs `error.<CODE>.message` and
 * `error.<CODE>.action` texts in every locale file; the manual lists them all.
 */
export const ERROR_CODES = [
  "INVALID_QUANTITY",
  "INVALID_PRICE",
  "INVALID_VAT_RATE",
  "CURRENCY_MISMATCH",
  "CUSTOMER_NAME_REQUIRED",
  "INVALID_EMAIL",
  "PLATE_REQUIRED",
  "INVALID_VIN",
  "IMPORT_FILE_UNKNOWN",
  "IMPORT_COLUMNS_MISSING",
  "IMPORT_NEEDS_MASTER_DATA",
  "ITEM_DESCRIPTION_REQUIRED",
  "WORK_ORDER_CLOSED",
  "WORK_ORDER_CHANGED",
  "WORK_ORDER_NOT_CLOSED",
  "REOPEN_REASON_REQUIRED",
  "VEHICLE_HAS_WORK_ORDERS",
  "CUSTOMER_HAS_WORK_ORDERS",
  "ARTICLE_NAME_REQUIRED",
  "ARTICLE_IN_USE",
  "STOCK_BY_MOVEMENT",
  "INVALID_STOCK_QUANTITY",
  "FEATURE_NOT_IN_PLAN",
  "QUOTE_NOTHING_PENDING",
  "QUOTE_NOT_FOUND",
  "QUOTE_EXPIRED",
  "QUOTE_ANSWERED",
  "INTAKE_NOT_FOUND",
  "INTAKE_EXPIRED",
  "INTAKE_SIGNED",
  "INTAKE_OBJECTION_SENT",
  "INTAKE_PENDING",
  "SIGNATURE_EXISTS",
  "READY_NOT_FOUND",
  "READY_EXPIRED",
  "NOT_OWNER",
  "NOT_ALLOWED",
  "REQUIRED_MISSING",
  "INVALID_ROLE",
  "INVITE_FAILED",
  "LAST_OWNER",
  "PASSWORD_TOO_SHORT",
  "PASSWORDS_DIFFER",
  "EMAIL_NOT_CONFIRMED",
  "SEND_FAILED",
  "REMINDER_NOTHING_TO_SEND",
  "VEHICLE_TYPE_DATA_MISSING",
  "VEHICLE_TYPE_MISSING",
  "VEHICLE_ENGINE_MISSING",
  "AI_CREDIT_EMPTY",
  "AI_FAILED",
  "AI_UNREADABLE",
  "AI_ALREADY_FILLED",
  "AI_PAUSED",
  "AI_DAILY_LIMIT",
  "AI_RATE_LIMIT",
  "AI_CREDIT_LOW",
  "AI_SUBSCRIPTION_BLOCKED",
  "AI_PURCHASE_TRIAL",
  "AI_PURCHASE_LIMIT",
  "SEATS_FULL",
  "READ_ONLY",
  "INVOICE_KEY_MISSING",
  "INVOICE_CUSTOMER_DATA",
  "INVOICE_FAILED",
  "INVOICE_UNKNOWN",
  "INVOICE_ISSUED",
  "INVOICE_CANCEL_REASON",
  "INVOICE_CANCEL_FAILED",
  "INVOICE_CANCEL_UNKNOWN",
  "TAXPAYER_KEY_MISSING",
  "TAXPAYER_INVALID_NUMBER",
  "TAXPAYER_NOT_FOUND",
  "TAXPAYER_FAILED",
  "FEEDBACK_UNAVAILABLE",
  "FEEDBACK_NOT_FOUND",
  "FEEDBACK_FAILED",
  "MFA_REQUIRED",
  "MFA_SETUP_REQUIRED",
  "MFA_CODE_INVALID",
  "MFA_TOO_MANY",
  "MFA_FAILED",
  "LOGO_TYPE",
  "LOGO_TOO_LARGE",
  "CUSTOM_LIMIT",
  "MECHANIC_NOT_MEMBER",
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export class DomainError extends Error {
  constructor(
    public readonly code: ErrorCode,
    /** Technical detail for support (e.g. why an outside service failed); shown small. */
    public readonly detail?: string,
  ) {
    super(detail ? `${code}: ${detail}` : code);
    this.name = "DomainError";
  }
  /** i18n key of the user facing message. */
  get messageKey(): string {
    return `error.${this.code}.message`;
  }
  /** i18n key of what the user should do. */
  get actionKey(): string {
    return `error.${this.code}.action`;
  }
}
